import { useState, useEffect } from 'react';
import { Droplets, Warehouse, Building2, CheckCircle2, ChevronDown } from 'lucide-react';

interface CalculationResult {
    flow: number;
    hydrants: number;
    steps: string[];
}

export default function WaterDefenseCalculator() {
    const [type, setType] = useState<'industrial' | 'office'>('industrial');
    const [surface, setSurface] = useState<number>(0);
    const [storageHeight, setStorageHeight] = useState<number>(0);
    const [hasSprinklers, setHasSprinklers] = useState<boolean>(false);
    const [result, setResult] = useState<CalculationResult | null>(null);

    const calculate = () => {
        if (surface <= 0) {
            setResult(null);
            return;
        }

        const steps: string[] = [];
        let flow = 0;
        let hydrants = 1;

        if (type === 'office') {
            // D9 Table 1 Logic
            steps.push("Using D9 Table 1 (Habitation/Offices)");
            if (surface <= 500) {
                flow = 60;
                hydrants = 1;
                steps.push("Surface <= 500m² -> Q = 60 m³/h");
            } else if (surface <= 2000) {
                flow = 120;
                hydrants = 2;
                steps.push("Surface <= 2000m² -> Q = 120 m³/h");
            } else if (surface <= 5000) {
                flow = 180;
                hydrants = 3;
                steps.push("Surface <= 5000m² -> Q = 180 m³/h");
            } else {
                flow = 240; // Simplified max for standard offices
                hydrants = 3;
                steps.push("Surface > 5000m² -> Q = 240 m³/h");
            }
        } else {
            // D9 Industrial Formula
            steps.push("Using D9 Industrial Formula");

            // Base Qi calculation
            // Qi = 30 * (S/500)
            let qi = 30 * (surface / 500);
            steps.push(`Base Qi = 30 * (${surface}/500) = ${qi.toFixed(2)} m³/h`);

            // Coefficients
            let coef = 0;

            // Storage Height Logic
            if (storageHeight > 12) {
                coef += 0.5;
                steps.push("+0.5 Coef (Storage > 12m)");
            } else if (storageHeight > 8) {
                coef += 0.2;
                steps.push("+0.2 Coef (Storage > 8m)");
            } else if (storageHeight > 3) {
                coef += 0.1;
                steps.push("+0.1 Coef (Storage > 3m)");
            }

            // Apply Coef
            let qRaw = qi * (1 + coef);
            steps.push(`Adjusted Q = ${qi.toFixed(2)} * (1 + ${coef}) = ${qRaw.toFixed(2)} m³/h`);

            // Risk Categories (simplified to Risk 2 for standard industrial)
            // Q2 = Qi * 1.5
            // Let's assume generic risk for this calculator version
            qRaw = qRaw * 1.5;
            steps.push("Risk Factor: Medium (x1.5)");

            if (hasSprinklers) {
                qRaw = qRaw / 2;
                steps.push("Sprinkler Protection: / 2");
            }

            // Min Flow Rule
            if (qRaw < 60) {
                qRaw = 60;
                steps.push("Applied Minimum Flow Rule (60 m³/h)");
            }

            flow = Math.ceil(qRaw);
            // Estimate hydrants (1 per 60m3/h is a good rule of thumb, though D9 is specific)
            hydrants = Math.ceil(flow / 60);
        }

        setResult({ flow, hydrants, steps });
    };

    useEffect(() => {
        calculate();
    }, [surface, type, storageHeight, hasSprinklers]);

    return (
        <div className="card-terminal p-6 animate-fadeIn">
            <div className="flex items-center gap-3 mb-6 border-b border-[var(--blueprint-line)] pb-4">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                    <Droplets className="w-6 h-6 text-cyan-500" />
                </div>
                <div>
                    <h2 className="text-xl font-display font-bold text-white">External Water Defense</h2>
                    <p className="text-sm text-gray-400">Document D9 (CNPP)</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Inputs */}
                <div className="space-y-6">
                    <div>
                        <label className="label-terminal">Facility Type</label>
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                onClick={() => setType('office')}
                                className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg border transition-all ${type === 'office'
                                        ? 'bg-cyan-500/20 border-cyan-500 text-white'
                                        : 'bg-transparent border-[var(--blueprint-line)] text-gray-400 hover:bg-white/5'
                                    }`}
                            >
                                <Building2 className="w-6 h-6" />
                                <span className="text-sm font-medium">Tertiary / Office</span>
                            </button>
                            <button
                                onClick={() => setType('industrial')}
                                className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-lg border transition-all ${type === 'industrial'
                                        ? 'bg-cyan-500/20 border-cyan-500 text-white'
                                        : 'bg-transparent border-[var(--blueprint-line)] text-gray-400 hover:bg-white/5'
                                    }`}
                            >
                                <Warehouse className="w-6 h-6" />
                                <span className="text-sm font-medium">Industrial</span>
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="label-terminal">Undivided Surface (m²)</label>
                        <input
                            type="number"
                            value={surface}
                            onChange={(e) => setSurface(Math.max(0, Number(e.target.value)))}
                            className="input-terminal"
                            placeholder="e.g. 2000"
                        />
                    </div>

                    {type === 'industrial' && (
                        <div className="animate-fadeIn space-y-6">
                            <div>
                                <label className="label-terminal">Max Storage Height (m)</label>
                                <input
                                    type="number"
                                    value={storageHeight}
                                    onChange={(e) => setStorageHeight(Math.max(0, Number(e.target.value)))}
                                    className="input-terminal"
                                    placeholder="e.g. 6"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setHasSprinklers(!hasSprinklers)}
                                    className={`w-6 h-6 rounded border flex items-center justify-center transition-colors ${hasSprinklers
                                            ? 'bg-cyan-500 border-cyan-500 text-white'
                                            : 'border-gray-500 text-transparent'
                                        }`}
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                </button>
                                <span className="text-sm text-gray-300">Sprinkler System Installed</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Results */}
                <div className="bg-black/40 rounded-xl p-6 border border-[var(--blueprint-line)] relative overflow-hidden flex flex-col min-h-[300px]">
                    <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                        <Droplets className="w-32 h-32" />
                    </div>

                    {result ? (
                        <>
                            <div className="relative z-10 flex-1">
                                <h3 className="text-lg font-semibold text-white mb-6">Hydraulic Requirements</h3>

                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div className="p-4 bg-white/5 rounded-lg border border-[var(--blueprint-line-light)] text-center">
                                        <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Required Flow</div>
                                        <div className="text-3xl font-display font-bold text-cyan-400">
                                            {result.flow} <span className="text-lg text-gray-500">m³/h</span>
                                        </div>
                                    </div>
                                    <div className="p-4 bg-white/5 rounded-lg border border-[var(--blueprint-line-light)] text-center">
                                        <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Hydrants</div>
                                        <div className="text-3xl font-display font-bold text-white">
                                            {result.hydrants} <span className="text-lg text-gray-500">x 100mm</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2 max-h-[150px] overflow-y-auto custom-scrollbar">
                                    <div className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">Calculation Trace</div>
                                    {result.steps.map((step, idx) => (
                                        <div key={idx} className="text-sm text-gray-400 font-mono pl-2 border-l border-cyan-500/30">
                                            {step}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-gray-500 relative z-10">
                            <Droplets className="w-12 h-12 mb-3 opacity-20" />
                            <p>Enter facility parameters</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
